import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  RelationshipType,
  LineageType,
  type FamilyMember,
  type FamilyTreeOwner,
  impliedGender,
  isInLaw,
  spouseOrdinal,
} from '../../services/family-tree.service'

interface MemberCardProps {
  member: FamilyMember
  owner?: FamilyTreeOwner | null
  allMembers?: FamilyMember[]
  onEdit?: (m: FamilyMember) => void
  onDelete?: (m: FamilyMember) => void
  variant?: 'node' | 'list'
}

/** Resolve a dynamic, gender-aware display label for a relationship. */
export function displayLabel(
  rel: RelationshipType,
  gender: 'MALE' | 'FEMALE',
  ownerGender?: 'MALE' | 'FEMALE' | null,
): string {
  switch (rel) {
    case RelationshipType.SIBLING:
      return gender === 'MALE' ? 'brother' : 'sister'
    case RelationshipType.CHILD:
      return gender === 'MALE' ? 'son' : 'daughter'
    case RelationshipType.SPOUSE:
      if (ownerGender === 'MALE') return 'wife'
      if (ownerGender === 'FEMALE') return 'husband'
      return 'spouse'
    case RelationshipType.GRANDPARENT_IN_LAW:
      return gender === 'MALE' ? 'grandfatherInLaw' : 'grandmotherInLaw'
    case RelationshipType.COUSIN_IN_LAW:
      return 'COUSIN_IN_LAW'
    default:
      return rel
  }
}

/** Returns " (n)" for spouses 2+, empty string otherwise. */
export function ordinalSuffix(ordinal: number | null): string {
  if (!ordinal || ordinal <= 1) return ''
  return ` (${ordinal})`
}

function GenderIcon({ gender }: { gender: 'MALE' | 'FEMALE' }) {
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
        gender === 'MALE' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
      }`}
      title={gender === 'MALE' ? 'Male' : 'Female'}
    >
      {gender === 'MALE' ? '♂' : '♀'}
    </span>
  )
}

export default function MemberCard({
  member,
  owner,
  allMembers,
  onEdit,
  onDelete,
  variant = 'node',
}: MemberCardProps) {
  const { t } = useTranslation()
  const ownerGender = owner?.gender ?? null
  const labelKey = displayLabel(member.relationship, member.gender, ownerGender)
  const inLaw = isInLaw(member.relationship)
  const implied = impliedGender(member.relationship)
  const ordinal = allMembers ? spouseOrdinal(member, allMembers) : null
  const suffix = ordinalSuffix(ordinal)

  const deceased = member.isDeceased
  const fullName = `${member.firstName}${member.lastName ? ' ' + member.lastName : ''}`

  return (
    <div
      className={`w-full rounded-lg border bg-white shadow-sm transition hover:shadow-md ${
        deceased ? 'border-neutral-300' : 'border-neutral-200'
      } ${variant === 'node' ? 'p-2.5' : 'p-3'}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <GenderIcon gender={member.gender} />
            <p className="truncate text-xs font-semibold text-neutral-900">{fullName}</p>
          </div>
          <p className="mt-0.5 truncate text-[11px] font-medium capitalize text-primary-700">
            {t(`familyTree.rels.${labelKey}`)}{suffix}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {inLaw && (
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                {t('familyTree.inLaws')}
              </span>
            )}
            {(member.relationship === RelationshipType.FATHER ||
              member.relationship === RelationshipType.MOTHER ||
              member.relationship === RelationshipType.FATHER_IN_LAW ||
              member.relationship === RelationshipType.MOTHER_IN_LAW ||
              member.relationship === RelationshipType.CHILD ||
              member.relationship === RelationshipType.SIBLING) && (
              <span
                className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${
                  member.lineageType === LineageType.ADOPTIVE
                    ? 'bg-purple-50 text-purple-700'
                    : 'bg-emerald-50 text-emerald-700'
                }`}
              >
                {member.lineageType === LineageType.ADOPTIVE
                  ? t('familyTree.adoptive')
                  : t('familyTree.biological')}
              </span>
            )}
            {deceased && (
              <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[9px] font-medium text-neutral-600">
                {t('familyTree.deceased')}
              </span>
            )}
          </div>
        </div>
      </div>

      {(onEdit || onDelete) && (
        <div className="mt-2 flex items-center justify-end gap-1 border-t border-neutral-100 pt-1.5">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(member)}
              className="rounded px-1.5 py-0.5 text-[10px] font-medium text-primary-700 hover:bg-primary-50"
            >
              {t('familyTree.editMember')}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(member)}
              className="rounded px-1.5 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-50"
            >
              {t('familyTree.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
